import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../../../lib/utils.js';
import { InputGroup } from '../../ui/input-group.js';
import { Input } from '../../ui/input.js';
import { SearchToken, SearchTokenClose, type SearchTokenStatus } from '../../ui/search-token.js';
import { ShellIconX } from '../app-shell/shell-stroke-icon.js';

export interface SearchTokenFieldToken {
  readonly prefix: string;
  readonly value?: string;
  readonly status: SearchTokenStatus;
  readonly onRemove?: () => void;
}

export interface SearchTokenFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'prefix'
> {
  tokens?: readonly SearchTokenFieldToken[];
  leading?: ReactNode;
  onClear?: () => void;
}

/**
 * Search `InputGroup` that hosts lockable facet tokens plus remaining free text.
 * Forbidden: toolbar `Tag` inside this field.
 */
export const SearchTokenField = forwardRef<HTMLInputElement, SearchTokenFieldProps>(
  function SearchTokenField({ className, tokens = [], leading, onClear, ...inputProps }, ref) {
    const rawValue = inputProps.value;
    const showClear = onClear !== undefined && typeof rawValue === 'string' && rawValue.length > 0;

    return (
      <InputGroup
        className={cn('search-token-field', className)}
        hasIcon={leading !== undefined && leading !== null}
      >
        {leading}
        {tokens.map((token) => (
          <SearchToken
            key={`${token.prefix}:${token.value ?? 'open'}`}
            prefix={token.prefix}
            {...(token.value !== undefined ? { value: token.value } : {})}
            status={token.status}
          >
            {token.onRemove !== undefined ? (
              <SearchTokenClose label={`Remover facet ${token.prefix}`} onClick={token.onRemove}>
                <ShellIconX size={10} />
              </SearchTokenClose>
            ) : null}
          </SearchToken>
        ))}
        <Input ref={ref} className="input--sm" {...inputProps} />
        {showClear ? (
          <SearchTokenClose label="Limpar busca" onClick={onClear}>
            <ShellIconX size={10} />
          </SearchTokenClose>
        ) : null}
      </InputGroup>
    );
  },
);
